// src/pages/admin/ManageApplications.js - Complete Application Management System
import React, { useState, useEffect } from 'react';
import {
  FaFileAlt,
  FaDownload,
  FaEye,
  FaCheck,
  FaTimes,
  FaUser,
  FaEnvelope,
  FaBriefcase,
  FaClock,
  FaFilter,
  FaSearch,
  FaSortAmountDown,
  FaStar,
  FaClipboardList,
  FaTrash,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ManageApplications = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [filterJob, setFilterJob] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const [currentRound, setCurrentRound] = useState(null);

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, []);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/careers/admin/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const updateApplicationStatus = async (appId, status, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/applications/${appId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status, notes }),
        }
      );

      if (response.ok) {
        alert(`Application ${status} successfully!`);
        fetchApplications();
        setSelectedApplication(null);
      }
    } catch (error) {
      console.error('Error updating application:', error);
      alert('Failed to update application status');
    }
  };

  const updateRoundStatus = async (appId, roundId, roundStatus, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/applications/${appId}/round`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ roundId, roundStatus, notes }),
        }
      );

      if (response.ok) {
        alert('Round status updated successfully!');
        fetchApplications();

        const updatedApp = await fetch(`${API_URL}/api/applications/${appId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (updatedApp.ok) {
          const data = await updatedApp.json();
          setSelectedApplication(data.application);
        }

        setShowNoteModal(false);
        setCurrentNote('');
        setCurrentRound(null);
      }
    } catch (error) {
      console.error('Error updating round:', error);
      alert('Failed to update round status');
    }
  };

  const downloadResume = async appId => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/applications/${appId}/resume`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `resume-${appId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to download resume');
      }
    } catch (error) {
      console.error('Error downloading resume:', error);
      alert('Failed to download resume');
    }
  };

  // View resume online using signed URL redirect
  const viewResume = async appId => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/applications/${appId}/resume`,
        {
          headers: { Authorization: `Bearer ${token}` },
          redirect: 'follow',
        }
      );

      if (response.ok) {
        // After redirects, response.url is the signed URL from Supabase
        window.open(response.url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to open resume');
      }
    } catch (error) {
      console.error('Error viewing resume:', error);
      alert('Failed to open resume');
    }
  };

  // Delete application (backend also deletes resume from Supabase)
  const deleteApplication = async appId => {
    if (
      !window.confirm(
        'Are you sure you want to delete this application and its resume?'
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/applications/${appId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        alert('Application deleted successfully');
        fetchApplications();
        if (selectedApplication && selectedApplication._id === appId) {
          setSelectedApplication(null);
        }
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to delete application');
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Failed to delete application');
    }
  };

  const openNoteModal = (round, action) => {
    setCurrentRound({ ...round, action });
    setShowNoteModal(true);
  };

  const handleRoundUpdate = () => {
    if (currentRound && selectedApplication) {
      updateRoundStatus(
        selectedApplication._id,
        currentRound.id,
        currentRound.action,
        currentNote
      );
    }
  };

  const filteredApplications = applications
    .filter(app => {
      if (filterJob !== 'all' && app.jobId !== filterJob) return false;
      if (filterStatus !== 'all' && app.status !== filterStatus) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          app.name.toLowerCase().includes(query) ||
          app.email.toLowerCase().includes(query) ||
          app.jobTitle.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.submittedAt) - new Date(a.submittedAt);
        case 'date-asc':
          return new Date(a.submittedAt) - new Date(b.submittedAt);
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
    hired: applications.filter(a => a.status === 'hired').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading applications...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
          <FaClipboardList className="text-blue-600" />
          Manage Applications
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Review and manage job applications
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
          <FaFileAlt className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Total Applications</div>
          <div className="text-4xl font-bold">{stats.total}</div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
          <FaClock className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Pending Review</div>
          <div className="text-4xl font-bold">{stats.pending}</div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
          <FaStar className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Shortlisted</div>
          <div className="text-4xl font-bold">{stats.shortlisted}</div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
          <FaTimes className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Rejected</div>
          <div className="text-4xl font-bold">{stats.rejected}</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-transform">
          <FaCheck className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Hired</div>
          <div className="text-4xl font-bold">{stats.hired}</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8">
        <div className="grid md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <FaSearch className="inline mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, email..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Filter by Job */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <FaFilter className="inline mr-2" />
              Filter by Job
            </label>
            <select
              value={filterJob}
              onChange={e => setFilterJob(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Jobs</option>
              {jobs.map(job => (
                <option key={job._id} value={job._id}>
                  {job.title}
                </option>
              ))}
            </select>
          </div>

          {/* Filter by Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="rejected">Rejected</option>
              <option value="hired">Hired</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <FaSortAmountDown className="inline mr-2" />
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredApplications.length} of {applications.length}{' '}
          applications
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        {filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <FaFileAlt className="text-6xl text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              No applications found
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    Experience
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    Applied On
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredApplications.map(app => (
                  <tr
                    key={app._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                          {app.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {app.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FaEnvelope className="text-xs" />
                            {app.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FaBriefcase className="text-blue-600" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {app.jobTitle}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                      {app.experience}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(app.submittedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          app.status === 'pending'
                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                            : app.status === 'shortlisted'
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                            : app.status === 'hired'
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                        }`}
                      >
                        {app.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedApplication(app)}
                          className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-600 dark:text-blue-400 transition-colors"
                          title="View Details"
                        >
                          <FaEye size={18} />
                        </button>
                        <button
                          onClick={() => viewResume(app._id)}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                          title="View Resume"
                        >
                          <FaFileAlt size={18} />
                        </button>
                        <button
                          onClick={() => downloadResume(app._id)}
                          className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900 text-green-600 dark:text-green-400 transition-colors"
                          title="Download Resume"
                        >
                          <FaDownload size={18} />
                        </button>
                        <button
                          onClick={() => deleteApplication(app._id)}
                          className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400 transition-colors"
                          title="Delete Application"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Application Details</h2>
                  <p className="text-sm opacity-90 mt-1">
                    Review and manage this application
                  </p>
                </div>
                <button
                  onClick={() => setSelectedApplication(null)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Applicant Info */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FaUser className="text-blue-600" />
                  Applicant Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">
                      Full Name
                    </label>
                    <p className="font-bold text-gray-900 dark:text-white text-lg">
                      {selectedApplication.name}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">
                      Email Address
                    </label>
                    <p className="font-bold text-gray-900 dark:text-white">
                      {selectedApplication.email}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">
                      Phone Number
                    </label>
                    <p className="font-bold text-gray-900 dark:text-white">
                      {selectedApplication.phone}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">
                      Experience
                    </label>
                    <p className="font-bold text-gray-900 dark:text-white">
                      {selectedApplication.experience}
                    </p>
                  </div>
                  <div className="md:col-span-2 p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <label className="text-sm text-gray-600 dark:text-gray-400 block mb-1">
                      Applied For
                    </label>
                    <p className="font-bold text-gray-900 dark:text-white text-lg">
                      {selectedApplication.jobTitle}
                    </p>
                  </div>
                </div>
              </div>

              {/* Cover Letter */}
              {selectedApplication.coverLetter && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Cover Letter
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-xl border-l-4 border-blue-600">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {selectedApplication.coverLetter}
                    </p>
                  </div>
                </div>
              )}

              {/* Selection Rounds */}
              {selectedApplication.roundStatuses &&
                selectedApplication.roundStatuses.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <FaClipboardList className="text-purple-600" />
                      Selection Rounds Progress
                    </h3>
                    <div className="space-y-4">
                      {selectedApplication.roundStatuses.map(
                        (round, index) => (
                          <div
                            key={round.id}
                            className={`p-4 rounded-xl border-2 ${
                              round.status === 'passed'
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                                : round.status === 'failed'
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-500'
                                : round.status === 'in_progress'
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                                : 'bg-gray-50 dark:bg-gray-700 border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                    round.status === 'passed'
                                      ? 'bg-green-500'
                                      : round.status === 'failed'
                                      ? 'bg-red-500'
                                      : round.status === 'in_progress'
                                      ? 'bg-blue-500'
                                      : 'bg-gray-400'
                                  }`}
                                >
                                  {index + 1}
                                </div>
                                <div>
                                  <h4 className="font-bold text-gray-900 dark:text-white">
                                    {round.title}
                                  </h4>
                                  <span
                                    className={`text-xs px-3 py-1 rounded-full font-semibold ${
                                      round.status === 'passed'
                                        ? 'bg-green-100 text-green-800'
                                        : round.status === 'failed'
                                        ? 'bg-red-100 text-red-800'
                                        : round.status === 'in_progress'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {round.status
                                      .replace('_', ' ')
                                      .toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              {round.status === 'in_progress' && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() =>
                                      openNoteModal(round, 'passed')
                                    }
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
                                  >
                                    <FaCheck /> Pass
                                  </button>
                                  <button
                                    onClick={() =>
                                      openNoteModal(round, 'failed')
                                    }
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors"
                                  >
                                    <FaTimes /> Fail
                                  </button>
                                </div>
                              )}
                            </div>
                            {round.notes && (
                              <div className="ml-13 p-3 bg-white dark:bg-gray-800 rounded-lg mt-2">
                                <strong className="text-gray-900 dark:text-white">
                                  Notes:
                                </strong>
                                <p className="text-gray-700 dark:text-gray-300 mt-1">
                                  {round.notes}
                                </p>
                              </div>
                            )}
                            {round.completedAt && (
                              <div className="ml-13 text-xs text-gray-500 mt-2">
                                Completed:{' '}
                                {new Date(
                                  round.completedAt
                                ).toLocaleString()}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Application Status Actions */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Update Application Status
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <button
                    onClick={() =>
                      updateApplicationStatus(
                        selectedApplication._id,
                        'shortlisted'
                      )
                    }
                    disabled={selectedApplication.status === 'shortlisted'}
                    className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 flex items-center justify-center gap-2 font-semibold"
                  >
                    <FaStar /> Shortlist
                  </button>
                  <button
                    onClick={() =>
                      updateApplicationStatus(
                        selectedApplication._id,
                        'rejected'
                      )
                    }
                    disabled={selectedApplication.status === 'rejected'}
                    className="p-4 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 flex items-center justify-center gap-2 font-semibold"
                  >
                    <FaTimes /> Reject
                  </button>
                  <button
                    onClick={() =>
                      updateApplicationStatus(
                        selectedApplication._id,
                        'hired'
                      )
                    }
                    disabled={selectedApplication.status === 'hired'}
                    className="p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 flex items-center justify-center gap-2 font-semibold"
                  >
                    <FaCheck /> Hire
                  </button>
                </div>
              </div>

              {/* Resume actions + delete */}
              <div className="grid md:grid-cols-3 gap-4">
                <button
                  onClick={() => viewResume(selectedApplication._id)}
                  className="p-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:from-gray-600 hover:to-gray-700 flex items-center justify-center gap-3 transition-all transform hover:scale-105 font-semibold text-lg"
                >
                  <FaEye size={20} /> View Resume
                </button>
                <button
                  onClick={() => downloadResume(selectedApplication._id)}
                  className="p-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 flex items-center justify-center gap-3 transition-all transform hover:scale-105 font-semibold text-lg"
                >
                  <FaDownload size={20} /> Download Resume
                </button>
                <button
                  onClick={() => deleteApplication(selectedApplication._id)}
                  className="p-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 flex items-center justify-center gap-3 transition-all transform hover:scale-105 font-semibold text-lg"
                >
                  <FaTrash size={20} /> Delete Application
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && currentRound && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {currentRound.action === 'passed' ? 'Pass' : 'Fail'} Round:{' '}
                {currentRound.title}
              </h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Add Notes (Optional)
              </label>
              <textarea
                value={currentNote}
                onChange={e => setCurrentNote(e.target.value)}
                rows="4"
                placeholder="Add feedback or notes about this round..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white resize-none"
              />
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleRoundUpdate}
                  className={`flex-1 px-6 py-3 text-white rounded-lg font-semibold transition-colors ${
                    currentRound.action === 'passed'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Confirm {currentRound.action === 'passed' ? 'Pass' : 'Fail'}
                </button>
                <button
                  onClick={() => {
                    setShowNoteModal(false);
                    setCurrentNote('');
                    setCurrentRound(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageApplications;
